import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { gateway } from 'libs/generated-grpc-web';
import { lastValueFrom } from 'rxjs';

import { BarcodeScanner } from '@app/src/app/barcodeScanner';
import { RootService } from '@app/src/app/minimal/services/RootService';
import { StreamManager } from '@app/src/app/minimal/services/StreamManager';
import { PeopleUpdated } from '@app/src/app/store/root/rootActions';

import {
  SystemAlertCloseEvents,
  SystemAlertModalHangTime,
  SystemAlertModalService,
  SystemAlertModalType,
} from '@shared/components/system-alert-modal';
import { SystemAlertSnackBarService } from '@shared/components/system-alert-snackbar';

import { PeopleSelectorService } from '../people-selector.service';
import { PeopleSelectorFormService } from '../ps-form.service';
import { PsCollectionSearchImplService } from '../search-impl/ps-collection-search.impl.service';

@Injectable()
export class PsArchiveService extends PeopleSelectorFormService<'Archive'> {
  /** Service Constructor */
  constructor(
    public router: Router,
    public snackbar: SystemAlertSnackBarService,
    public barCodeScanner: BarcodeScanner,
    public peopleSelector: PeopleSelectorService,
    private _rootService: RootService,
    private _systemModal: SystemAlertModalService,
    private _psCollectionSearch: PsCollectionSearchImplService,
    private _streamManager: StreamManager,
    private _peopleAdminManager: gateway.people_ng_grpc_pb.PeopleAdminManager,
    private _store: Store<any>,
  ) {
    super(
      {
        name: 'Archive',
        pageDefinitions: {
          add: {
            submitFn: async => this.submitArchive(),
            searchFn: async => this._psCollectionSearch.collectionSearch('text', undefined),
          },
        },
      },
      router,
      snackbar,
      barCodeScanner,
      peopleSelector,
    );
    this.setConfig({
      name: 'Archive',
      pageDefinitions: {
        add: {
          submitFn: async () => this.submitArchive(),
          searchFn: async (type, filters) => this._psCollectionSearch.collectionSearch(type, filters, true),
        },
      },
    });
  }

  public async submitArchive(): Promise<void> {
    const selection = this.selection.getSelection();
    const emails = selection.map(person => person.email);
    if (emails.length) {
      const modalRef = await this._systemModal.open({
        modalType: SystemAlertModalType.WARNING,
        heading: 'Archive',
        message: `You are about to archive ${emails.length} ${
          emails.length === 1 ? 'person' : 'people'
        }. This action CANNOT be undone.`,
        closeBtn: 'Cancel',
        confirmActionBtn: 'Confirm',
      });

      const response = await lastValueFrom(modalRef.afterClosed());

      if (response?.type === SystemAlertCloseEvents.CONFIRM) {
        const requestStartedModalRef = await this._systemModal.open({
          heading: 'Archiving Started',
          message: 'Archiving will start shortly, this may take a while',
          modalType: SystemAlertModalType.DEFAULT,
          hangTime: SystemAlertModalHangTime.LONG,
        });
        await requestStartedModalRef.afterClosed().toPromise();
        await this._rootService.addLoadingPromise(this._archivePeople(emails));
        const completeModalRef = await this._systemModal.open({
          heading: 'Success',
          message: 'Archiving complete',
          modalType: SystemAlertModalType.SUCCESS,
          hangTime: SystemAlertModalHangTime.SHORT,
        });
        await completeModalRef.afterClosed().toPromise();
        await this._streamManager.restartStreamIfAvailable('SearchPeople');
      }
    }
  }

  private async _archivePeople(emails: string[]) {
    const request = new gateway.people_pb.ArchivePeopleBulkRequest();
    emails.forEach(email => {
      request.addEmail(email);
    });
    await this._peopleAdminManager.archivePeopleBulk(request).toPromise();
    this._streamManager.restartStreamIfAvailable('AdminPeopleFeed');
    this._store.dispatch(new PeopleUpdated());
  }
}
